import type { Transition, Variants } from 'framer-motion';

/**
 * Shared motion language for the investor run-through.
 *
 * Before this module every reveal was a hand-written
 * `initial/whileInView/transition` triple with a copy-pasted
 * `delay: base + i * step`. That produced one motion idea (fade + rise)
 * repeated forty times, so the deck read as a single flat gesture.
 *
 * The vocabulary here is deliberately small — three curves, one reveal
 * primitive, one orchestration primitive — so sections can differ in
 * *timing* while staying unmistakably the same product.
 */

/** Expo-out. The house curve: fast departure, long graceful settle. */
export const EASE_EXPO = [0.16, 1, 0.3, 1] as const;

/** Softer expo, for large surfaces where the house curve reads as a snap. */
export const EASE_SOFT = [0.22, 0.9, 0.28, 1] as const;

/** Symmetrical ease for ambient loops that should never feel like an event. */
export const EASE_AMBIENT = [0.45, 0, 0.55, 1] as const;

/** Default viewport gate — reveal once, a fifth of the way in. */
export const VIEWPORT = { once: true, amount: 0.2 } as const;

/** Tighter gate for small elements that sit low inside a tall section. */
export const VIEWPORT_TIGHT = { once: true, amount: 0.4 } as const;

/**
 * Orchestration parent. Children with `revealItem` variants inherit the
 * stagger, so per-index delay arithmetic disappears from the callsites.
 */
export function revealGroup(stagger = 0.07, delayChildren = 0): Variants {
  return {
    hidden: {},
    visible: {
      transition: { staggerChildren: stagger, delayChildren },
    },
  };
}

/** The reveal primitive: rise and fade on the house curve. */
export function revealItem(distance = 16, duration = 0.6): Variants {
  return {
    hidden: { opacity: 0, y: distance },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration, ease: EASE_EXPO },
    },
  };
}

/** Lateral variant, for list rows that read left-to-right. */
export function revealFrom(distance = -10, duration = 0.5): Variants {
  return {
    hidden: { opacity: 0, x: distance },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration, ease: EASE_EXPO },
    },
  };
}

/**
 * Headline mask reveal — the type rises out from behind its own baseline
 * rather than fading in as a block. Bottom inset is animated on a wrapper
 * that carries a little bottom padding so descenders are never clipped.
 *
 * `reduced` swaps to a plain fade: clip-path animation is motion the
 * `prefers-reduced-motion` contract should suppress, and framer-motion's
 * `MotionConfig reducedMotion="user"` only neutralises transforms.
 */
export function maskReveal(reduced: boolean, duration = 0.9): Variants {
  if (reduced) {
    return {
      hidden: { opacity: 0 },
      visible: { opacity: 1, transition: { duration: 0.3 } },
    };
  }
  return {
    hidden: { opacity: 0, y: '0.28em', clipPath: 'inset(0% 0% 100% 0%)' },
    visible: {
      opacity: 1,
      y: '0em',
      clipPath: 'inset(0% 0% 0% 0%)',
      transition: { duration, ease: EASE_SOFT },
    },
  };
}

/** Hairline rule that draws out from its leading edge. */
export const drawLine: Variants = {
  hidden: { scaleX: 0 },
  visible: {
    scaleX: 1,
    transition: { duration: 0.7, ease: EASE_EXPO },
  },
};

/** Bar/meter fill. Width is supplied per-instance via a custom value. */
export function fillBar(width: string, duration = 1.1): Variants {
  return {
    hidden: { width: '0%' },
    visible: {
      width,
      transition: { duration, ease: EASE_EXPO },
    },
  };
}

/**
 * The single hover gesture for every raised card on the site. Previously
 * six components used `whileHover={{ y: -3 }}` and the rest used CSS
 * `hover:-translate-y-0.5`, so cards answered the cursor inconsistently.
 */
export const HOVER_LIFT: Transition = { duration: 0.25, ease: EASE_EXPO };
